/**
 * useFestival — global state for the running Full Value promotion.
 *
 * One poll, shared by every consumer via a module-level cache, because the
 * banner, the shop grid and the wallet all need the same countdown and none of
 * them should each hit the API on every render.
 */
import { useEffect, useState } from 'react';
import { api } from '../utils/api';
import storage from '../utils/storage';

let cache = null;
let cacheAt = 0;
let cacheToken = null;
let inflight = null;
const listeners = new Set();
const TTL = 45000;

const fetchState = async () => {
  if (inflight) return inflight;
  const token = storage.getToken() || '';
  inflight = api.get('/festival/state')
    .then((r) => {
      cache = r.data;
      cacheAt = Date.now();
      cacheToken = token;
      listeners.forEach((fn) => fn(cache));
      return cache;
    })
    .catch(() => cache)
    .finally(() => { inflight = null; });
  return inflight;
};

export const useFestival = () => {
  const [state, setState] = useState(cache);

  useEffect(() => {
    listeners.add(setState);
    // Refetch when the session changes too — the payload carries the caller's
    // own coin value, so a state cached before login has no `you` block.
    const stale = () => !cache
      || Date.now() - cacheAt > TTL
      || cacheToken !== (storage.getToken() || '');
    if (stale()) fetchState();
    // Cheap 4s token check so the numbers personalise right after login rather
    // than on the next poll.
    const t = setInterval(() => { if (stale()) fetchState(); }, 4000);
    return () => { listeners.delete(setState); clearInterval(t); };
  }, []);

  return state;
};

/** Live countdown to the event end, ticking once a second. */
export const useCountdown = (endsAtIso) => {
  const [left, setLeft] = useState(() => msLeft(endsAtIso));
  useEffect(() => {
    setLeft(msLeft(endsAtIso));
    const t = setInterval(() => setLeft(msLeft(endsAtIso)), 1000);
    return () => clearInterval(t);
  }, [endsAtIso]);
  const total = Math.max(0, Math.floor(left / 1000));
  return {
    total,
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  };
};

function msLeft(iso) {
  if (!iso) return 0;
  return Math.max(0, new Date(iso).getTime() - Date.now());
}

export default useFestival;
